import fs from 'node:fs/promises';
import path from 'node:path';

import { isSpecFile, specExtension, splitSpecExtension } from '../brand.js';

/** Build a map of `workflowId` -> spec file path across a directory tree. */
export async function indexSpecFiles(dir: string): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  let entries: string[];
  try {
    entries = await fs.readdir(dir, { recursive: true });
  } catch {
    return map; // directory doesn't exist yet
  }
  const ID_RE = /^workflowId:\s*([0-9a-f-]{36})\s*$/im;
  for (const rel of entries) {
    if (!isSpecFile(rel)) continue;
    const full = path.join(dir, rel);
    try {
      const src = await fs.readFile(full, 'utf-8');
      const m = ID_RE.exec(src);
      if (m && !map.has(m[1])) map.set(m[1], full);
    } catch {
      // unreadable — skip
    }
  }
  return map;
}

/**
 * Index several spec roots and merge into one `workflowId` -> path map.
 * Roots are processed in order and the FIRST occurrence of a `workflowId` wins,
 * so callers pass roots highest-precedence-first.
 */
export async function mergeSpecRoots(roots: string[]): Promise<Map<string, string>> {
  const byWorkflowId = new Map<string, string>();
  for (const root of roots) {
    for (const [id, file] of await indexSpecFiles(root)) {
      if (!byWorkflowId.has(id)) byWorkflowId.set(id, file);
    }
  }
  return byWorkflowId;
}

/** Pick a collision-free filename, disambiguating with a short id when needed. */
export function uniqueName(slug: string, workflowId: string, used: Set<string>): string {
  let name = `${slug}${specExtension}`;
  if (used.has(name)) name = `${slug}-${workflowId.slice(0, 8)}${specExtension}`;
  return name;
}

export function stampedSpecPath(file: string): string {
  const split = splitSpecExtension(file);
  if (split) {
    return `${split.base}.stamped${split.ext}`;
  }
  return `${file}.stamped${specExtension}`;
}

/** Kebab-case slug for the default pull filename. */
export function slugify(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug || 'workflow';
}
